import { ObjectId } from "bson";
import type { ViewType } from "@/models/types/DatabaseSource";
import { BoardProperty, DatabaseSource, View, ViewCollection } from "@/types/board";
import type { Block } from "@/types/block";
import { seedStatusGroups } from "./statusGroups";

export interface CollectionViewTemplateOptions {
  viewType: ViewType;
  title?: string;
  icon?: string;
  workspaceId: string;
  userEmail: string;
  blockId: string; // The collection_view block ID
  workareaId?: string | null;
}

/**
 * Creates a datasource with appropriate properties based on viewtype
 * For board viewtype, creates a status property with default options
 */
export function createDataSource(
  options: CollectionViewTemplateOptions
): DatabaseSource {
  const { viewType, workspaceId, userEmail, blockId } = options;

  const dataSourceId = new ObjectId().toString();
  const properties: Record<string, BoardProperty> = {};

  if (viewType === "board" || viewType === "chart") {
    const statusPropertyId = `prop_${new ObjectId()}`;
    const statusOptions = [
      { id: `opt_${new ObjectId()}`, name: "Todo", color: "gray" },
      { id: `opt_${new ObjectId()}`, name: "In Progress", color: "blue" },
      { id: `opt_${new ObjectId()}`, name: "Done", color: "green" },
    ];

    properties[statusPropertyId] = {
      name: "Status",
      type: "status",
      options: statusOptions,
      groups: seedStatusGroups(statusOptions),
      default: true, // This is the default status property
      showProperty: true,
    };
  }

  // Form boards start with two default questions — their properties are
  // created here and mapped via formQuestions on the view below.
  if (viewType === "forms") {
    const textPropertyId = `prop_${new ObjectId()}`;
    properties[textPropertyId] = {
      name: "Text",
      type: "text",
      default: true,
      showProperty: true,
    };
    const multiSelectPropertyId = `prop_${new ObjectId()}`;
    properties[multiSelectPropertyId] = {
      name: "Multi-Select",
      type: "multi_select",
      options: [
        { id: `opt_${new ObjectId()}`, name: "Option 1" },
        { id: `opt_${new ObjectId()}`, name: "Option 2" },
        { id: `opt_${new ObjectId()}`, name: "Option 3" },
      ],
      default: true,
      showProperty: true,
    };
  }

  // For calendar and timeline views, create date property
  if (viewType === "calendar" || viewType === "timeline") {
    const datePropertyId = `prop_${new ObjectId()}`;
    properties[datePropertyId] = {
      name: "Date",
      type: "date",
      default: true,
      showProperty: true,
    };
  }

  // For forms view, create text property
  if (viewType === "forms") {
    const textPropertyId = `prop_${new ObjectId()}`;
    properties[textPropertyId] = {
      name: "Name",
      type: "text",
      default: true,
      showProperty: true,
    };
  }

  // Reserved property id for the optional ID column (actual schema is created when the user adds "ID").
  const reservedUniqueIdPropertyId = `prop_${new ObjectId()}`;

  // Create the datasource
  const dataSource: DatabaseSource = {
    _id: dataSourceId,
    title: "New Datasource",
    createdBy: {
      userId: userEmail,
      userName: userEmail,
      userEmail,
    },
    createdAt: new Date(),
    updatedAt: new Date(),
    properties,
    settings: {
      uniqueIdSetting: {
        propertyId: reservedUniqueIdPropertyId,
        lastUniqueId: 0,
      },
    },
    workspaceId,
    isSprint: false,
    isSprintOn: false,
    blockIds: [], // Will be populated as pages are added
    mainView: blockId,
    defaultViewType: (viewType as DatabaseSource["defaultViewType"]) ?? "board",
  };

  return dataSource;
}

/**
 * Creates a viewtype that references the datasource
 */
export function createViewType(
  options: CollectionViewTemplateOptions,
  dataSource: DatabaseSource,
  overrideViewType?: ViewType,
  overrideTitle?: string
): View {
  const { viewType, blockId } = options;
  const actualViewType = overrideViewType || viewType;

  const viewTypeId = new ObjectId().toString();

  // For list views, add the status property ID to propertyVisibility
  // For board views, set the status property ID as the default group
  const propertyVisibility: { propertyId: string }[] = [];
  let groupPropertyId = "";

  if (dataSource.properties) {
    // Find the status property ID
    const statusPropertyId = Object.keys(dataSource.properties).find(
      (propId) => dataSource.properties?.[propId]?.type === "status"
    );

    if (statusPropertyId) {
      if (actualViewType === "list") {
        propertyVisibility.push({ propertyId: statusPropertyId });
      } else if (actualViewType === "board") {
        groupPropertyId = statusPropertyId;
      }
    }
  }

  let group;

  if (groupPropertyId) {
    group = {
      propertyId: groupPropertyId,
      // colorColumn: "",
      // sortDirection: "ascending",
      // hideEmptyGroups: false,
    }
  }


  const viewTypeObj: View = {
    _id: viewTypeId,
    viewType: actualViewType,
    icon: "",
    title: overrideTitle || actualViewType.charAt(0).toUpperCase() + actualViewType.slice(1),
    databaseSourceId: dataSource._id,
    viewDatabaseId: blockId,
    settings: {
      propertyVisibility,
      filters: [],
      advancedFilters: [],
      group
    },
  };

  return viewTypeObj;
}

function findStatusPropertyId(dataSource: DatabaseSource): string | undefined {
  return Object.keys(dataSource.properties || {}).find(
    (pid) => dataSource.properties?.[pid]?.type === "status",
  );
}

/** One empty datasource row per status option so a new chart isn't blank. */
function buildStatusSeedRows(
  dataSource: DatabaseSource,
  options: CollectionViewTemplateOptions,
): Block[] {
  const { workspaceId, userEmail, workareaId } = options;
  const statusPropId = findStatusPropertyId(dataSource);
  if (!statusPropId) return [];
  const statusOptions = dataSource.properties?.[statusPropId]?.options || [];

  return statusOptions.map((opt: any) => ({
    _id: new ObjectId().toString(),
    blockType: "page" as const,
    workspaceId,
    workareaId: workareaId ?? null,
    parentId: dataSource._id,
    parentType: "collection" as const,
    value: {
      title: "",
      pageType: "Viewdatabase_Note",
      databaseProperties: { [statusPropId]: opt.id },
      icon: "",
      coverUrl: null,
      userId: userEmail,
      userEmail,
    } as any,
    status: "alive" as const,
    blockIds: [],
  }));
}

/**
 * Creates a complete collection_view template with datasource and viewtype
 * This is the main function to use when creating a new collection_view block
 * For forms viewType, creates 2 views: form view and list view for responses
 * For chart viewType, creates a secondary "Data" list view, defaults the chart's
 * X-axis to Status, and returns seeded rows (one per status option).
 */
export function createCollectionViewTemplate(
  options: CollectionViewTemplateOptions
): {
  viewDatabase: ViewCollection;
  dataSource: DatabaseSource;
  viewType: View;
  seededRows: Block[];
} {
  const { viewType, title, icon, userEmail } = options;

  // Step 1: Create datasource with appropriate properties
  const dataSource = createDataSource(options);

  // Step 2: Create viewtype(s) that reference the datasource
  const viewTypeObj = createViewType(options, dataSource);

  // For forms viewType, also create a list view for responses
  const viewsTypes: View[] = [viewTypeObj];
  if (viewType === "forms") {
    viewTypeObj.formQuestions = Object.entries(dataSource.properties || {})
      .filter(([, prop]) => prop.type === "text" || prop.type === "multi_select")
      .map(([propertyId, prop]) => ({ id: new ObjectId().toString(), propertyId, name: prop.name }));
    const responseListView = createViewType(options, dataSource, "list", "Responses");
    viewsTypes.push(responseListView);
  }

  let seededRows: Block[] = [];
  if (viewType === "chart") {
    const statusPropId = findStatusPropertyId(dataSource);

    if (statusPropId) {
      viewTypeObj.settings = {
        ...(viewTypeObj.settings || {}),
        chart: {
          chartType: "verticalBar",
          xAxis: { propertyId: statusPropId },
          yAxis: { whatToShow: "count" },
        },
      } as any;
    }

    const dataView = createViewType(options, dataSource, "list", "Data");
    dataView.settings = {
      ...(dataView.settings || {}),
      propertyVisibility: statusPropId ? [{ propertyId: statusPropId }] : [],
    };
    viewsTypes.push(dataView);

    seededRows = buildStatusSeedRows(dataSource, options);
    dataSource.blockIds = seededRows.map((r) => r._id);
  }

  // Step 3: Create the viewDatabase structure
  const viewDatabase: ViewCollection = {
    title: title ?? "",
    icon: icon || "",
    viewsTypes,
    createdBy: {
      userId: userEmail,
      userName: userEmail,
      userEmail,
    },
    defaultDataSourceId: dataSource._id.toString(),
  };

  return {
    viewDatabase,
    dataSource,
    viewType: viewTypeObj,
    seededRows,
  };
}

/**
 * Serializes a datasource for API transmission
 * Converts ObjectIds to strings and dates to ISO strings for JSON
 */
export function serializeDataSourceForAPI(dataSource: DatabaseSource): any {
  return {
    _id: dataSource._id.toString(),
    ...(dataSource.title && { title: dataSource.title }),
    createdBy: {
      userId: typeof dataSource.createdBy.userId === "string"
        ? dataSource.createdBy.userId
        : dataSource.createdBy.userId,
      userName: dataSource.createdBy.userName || "",
      userEmail: dataSource.createdBy.userEmail,
    },
    createdAt: dataSource.createdAt.toISOString(),
    updatedAt: dataSource.updatedAt?.toISOString(),
    properties: dataSource.properties || {},
    settings: dataSource.settings || {},
    ...(dataSource.workspaceId && { workspaceId: dataSource.workspaceId }),
    isSprint: dataSource.isSprint ?? false,
    isSprintOn: dataSource.isSprintOn ?? false,
    ...(dataSource.pairedDataSourceId && { pairedDataSourceId: dataSource.pairedDataSourceId }),
    blockIds: dataSource.blockIds || [],
    mainView: dataSource.mainView || "board",
    ...(dataSource.defaultViewType && { defaultViewType: dataSource.defaultViewType }),
  };
}

/**
 * Serializes viewDatabase for API transmission
 * Converts ObjectIds in viewsTypes to strings
 */
export function serializeViewDatabaseForAPI(viewDatabase: ViewCollection): any {
  return {
    ...viewDatabase,
    viewsTypes: viewDatabase.viewsTypes?.map((vt) => ({
      ...vt,
      _id: vt._id || undefined,
      databaseSourceId: vt.databaseSourceId || undefined,
      viewDatabaseId: vt.viewDatabaseId || undefined,
    })) || [],
    ...(viewDatabase.defaultDataSourceId && { defaultDataSourceId: viewDatabase.defaultDataSourceId }),
  };
}

